import React, { useState } from 'react';
import {
    AppBar, Toolbar, Button, Box, Drawer, List,
    ListItem, ListItemText, IconButton, Divider
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const NavbarMain = () => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, logout, user } = useAuth();

    const handleDrawerToggle = () => setMobileOpen(!mobileOpen);

    const scrollToSection = (sectionId) => {
        if (location.pathname === '/') {
            const element = document.getElementById(sectionId);
            if (element) {
                const offsetPosition = element.getBoundingClientRect().top + window.pageYOffset - 80;
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
            }
        } else {
            navigate('/');
            setTimeout(() => {
                const element = document.getElementById(sectionId);
                if (element) {
                    const offsetPosition = element.getBoundingClientRect().top + window.pageYOffset - 80;
                    window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
                }
            }, 100);
        }
        setMobileOpen(false);
    };

    const scrollToTop = () => {
        if (location.pathname === '/') {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
            navigate('/');
            setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 100);
        }
        setMobileOpen(false);
    };

    const handleProfileClick = () => {
        if (user?.role === 'ADMIN') {
            navigate('/admin');
        } else {
            navigate('/profile');
        }
        setMobileOpen(false);
    };

    // Ажурирано мени со Тренинг Програми и Диети
    const menuItems = [
        { text: 'Почетна', path: '/', onClick: () => scrollToTop() },
        { text: 'Тренинг Програми', path: '/programs' }, // Ново
        { text: 'Диети', path: '/diets' },               // Ново
        { text: 'За Нас', path: '/about' },
        { text: 'Достигнување', path: '/achievements' },
        { text: 'Блог', path: '/blog' },
        { text: 'Изработка на Веб-страни', onClick: () => scrollToSection('advertisement') },
        { text: 'Контакт', onClick: () => scrollToSection('contact') }
    ];

    const drawer = (
        <Box sx={{ width: 260, height: '100%', display: 'flex', flexDirection: 'column' }}>

            {/* ===== HEADER со лого и X ===== */}
            <Box sx={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between',
                px: 2, py: 1.5,
                borderBottom: '1px solid rgba(255,126,185,0.2)',
                backgroundColor: 'rgba(255,126,185,0.04)'
            }}>
                <Box onClick={scrollToTop} sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
                    <Box
                        component="img"
                        src="/Logo.png"
                        alt="Logo"
                        sx={{ height: 48, width: 'auto', objectFit: 'contain' }}
                    />
                </Box>
                <IconButton onClick={handleDrawerToggle} sx={{ color: '#ff7eb9' }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* ===== MENU ITEMS ===== */}
            <List sx={{ flex: 1, px: 1, py: 1 }}>
                {menuItems.map((item) => {
                    const isActive = location.pathname === item.path;
                    return (
                        <ListItem
                            key={item.text}
                            disablePadding
                            onClick={() => {
                                if (item.onClick) item.onClick();
                                else { navigate(item.path); setMobileOpen(false); }
                            }}
                            sx={{
                                borderRadius: '10px', mb: 0.5,
                                backgroundColor: isActive ? 'rgba(255,126,185,0.12)' : 'transparent',
                                '&:hover': { backgroundColor: 'rgba(255,126,185,0.08)' },
                                cursor: 'pointer'
                            }}
                        >
                            <ListItemText
                                primary={item.text}
                                sx={{
                                    px: 2, py: 0.2,
                                    '& .MuiListItemText-primary': {
                                        fontSize: '0.95rem',
                                        color: isActive ? '#ff7eb9' : '#555',
                                        fontWeight: isActive ? 'bold' : 'normal'
                                    }
                                }}
                            />
                        </ListItem>
                    );
                })}
            </List>

            {/* ===== DIVIDER + PROFILE/LOGIN ===== */}
            <Divider sx={{ borderColor: 'rgba(255,126,185,0.2)' }} />
            <Box sx={{ px: 2, py: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                {isAuthenticated ? (
                    <>
                        <Button
                            fullWidth
                            variant="contained"
                            onClick={handleProfileClick}
                            sx={{
                                backgroundColor: '#ff7eb9', color: 'white',
                                borderRadius: '10px', fontWeight: 'bold',
                                '&:hover': { backgroundColor: '#ff4a97' }
                            }}
                        >
                            Мој Профил
                        </Button>
                        <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => { logout(); navigate('/login'); setMobileOpen(false); }}
                            sx={{
                                color: '#ff4a97', borderColor: '#ff4a97',
                                borderRadius: '10px', fontWeight: 'bold',
                                '&:hover': { backgroundColor: 'rgba(255,74,151,0.08)' }
                            }}
                        >
                            Одјави се
                        </Button>
                    </>
                ) : (
                    <Button
                        fullWidth
                        variant="contained"
                        onClick={() => { navigate('/login'); setMobileOpen(false); }}
                        sx={{
                            backgroundColor: '#ff7eb9', color: 'white',
                            borderRadius: '10px', fontWeight: 'bold',
                            '&:hover': { backgroundColor: '#ff4a97' }
                        }}
                    >
                        Најави се
                    </Button>
                )}
            </Box>
        </Box>
    );

    return (
        <>
            <AppBar position="fixed" sx={{
                backgroundColor: 'rgba(255,255,255,0.98)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 2px 20px rgba(0,0,0,0.1)',
                color: '#333', zIndex: 9999
            }}>
                <Toolbar sx={{ py: 0, px: 2, minHeight: { xs: '70px', md: '100px' } }}>

                    {/* Logo */}
                    <Box sx={{
                        display: 'flex', alignItems: 'center',
                        flexGrow: 1, cursor: 'pointer',
                        height: { xs: '70px', md: '100px' }
                    }} onClick={scrollToTop}>
                        <Box
                            component="img"
                            src="/Logo.png"
                            alt="Logo"
                            sx={{
                                height: '100%', width: 'auto',
                                objectFit: 'contain', borderRadius: '8px',
                                transition: 'transform 0.2s ease',
                                '&:hover': { transform: 'scale(1.05)' }
                            }}
                        />
                    </Box>

                    {/* Desktop Menu */}
                    <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, alignItems: 'center' }}>
                        {menuItems.map((item) => (
                            <Button
                                key={item.text}
                                color="inherit"
                                onClick={item.onClick || (() => navigate(item.path))}
                                sx={{
                                    fontWeight: location.pathname === item.path ? 'bold' : 'normal',
                                    color: location.pathname === item.path ? '#ff7eb9' : '#666',
                                    fontSize: '15px', // Малку намален фонт за да ги собере сите ставки
                                    whiteSpace: 'nowrap',
                                    '&:hover': { backgroundColor: 'rgba(255,126,185,0.1)', color: '#ff7eb9' }
                                }}
                            >
                                {item.text}
                            </Button>
                        ))}

                        {isAuthenticated ? (
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                <Button
                                    onClick={handleProfileClick}
                                    sx={{
                                        backgroundColor: '#ff7eb9', color: 'white',
                                        fontWeight: 'bold', px: 3, fontSize: '15px',
                                        borderRadius: '8px',
                                        '&:hover': { backgroundColor: '#ff4a97', transform: 'translateY(-2px)' },
                                        transition: 'all 0.2s ease'
                                    }}
                                >
                                    Мој Профил
                                </Button>
                                <Button
                                    onClick={() => { logout(); navigate('/login'); }}
                                    sx={{
                                        color: '#ff4a97', fontWeight: 'bold', fontSize: '15px',
                                        '&:hover': { backgroundColor: 'rgba(255,74,151,0.1)' }
                                    }}
                                >
                                    Одјави се
                                </Button>
                            </Box>
                        ) : (
                            <Button
                                onClick={() => navigate('/login')}
                                sx={{
                                    backgroundColor: '#ff7eb9', color: 'white',
                                    fontWeight: 'bold', px: 3, fontSize: '15px',
                                    borderRadius: '8px',
                                    '&:hover': { backgroundColor: '#ff4a97', transform: 'translateY(-2px)' },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Најави се
                            </Button>
                        )}
                    </Box>

                    {/* Mobile Menu Button */}
                    <IconButton
                        color="inherit"
                        onClick={handleDrawerToggle}
                        sx={{ display: { md: 'none' }, color: '#ff7eb9' }}
                    >
                        <MenuIcon />
                    </IconButton>
                </Toolbar>
            </AppBar>

            <Drawer
                variant="temporary"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 260 }
                }}
            >
                {drawer}
            </Drawer>

            <Toolbar sx={{ minHeight: { xs: '70px', md: '100px' } }} />
        </>
    );
};

export default NavbarMain;